/**
 * MapReflow — invisible helper that lives inside a <MapContainer> and forces
 * Leaflet to recompute its dimensions whenever the container becomes visible
 * or is resized.
 *
 * Why: Leaflet caches its container size on init. If the map is rendered
 * inside a flex/grid slot whose final size is only known after fonts, images
 * or lazy chunks have loaded, tiles are drawn using the initial (partial)
 * size and only half of the map area is filled with tiles. The rest stays
 * grey until the user manually pans/zooms.
 *
 * Fix: run `invalidateSize()`
 *   1. on mount (deferred to next tick + a longer safety tick),
 *   2. on window resize,
 *   3. whenever the wrapping DOM element itself changes size (ResizeObserver).
 *
 * Drop this inside any <MapContainer> — no props required.
 */
import { useEffect } from "react";
import { useMap } from "react-leaflet";

const MapReflow = () => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const reflow = () => {
      try { map.invalidateSize(false); } catch { /* Leaflet transient */ }
    };

    // Immediate reflows: handles the "flex slot resolves after mount" case.
    const t1 = setTimeout(reflow, 60);
    const t2 = setTimeout(reflow, 350);
    const t3 = setTimeout(reflow, 900);

    // Window resize (address bar collapse on mobile, orientation change, ...).
    window.addEventListener("resize", reflow);

    // Element-level resize (parent switches display, sidebar toggles, etc.).
    let ro = null;
    const container = map.getContainer();
    if (container && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(reflow);
      ro.observe(container);
    }

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("resize", reflow);
      if (ro) ro.disconnect();
    };
  }, [map]);

  return null;
};

export default MapReflow;
